import React, { useState } from 'react';

import {
  trans,
  useSoyuzLocales,
  useSoyuzClientConfig,
  getPagePath
} from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-metrics';

import ReCaptcha from 'react-google-recaptcha';
import * as yup from 'yup';

import { useStateMachineContext } from '@common/context/StateMachineContext';

import graphqlClient from '@gqlClient/client';
import createZendeskTicket from '@gqlClient/mutations/zendesk';

import FormProvider from '../FormProvider';
import FormError from '../FormError';

import {
  TENANT_ROLE,
  TENANT_REASONS,
  LANDLORD_REASONS,
  FORM_FIELDS
} from '../../constants';

import ContactUsHeader from '../ContactUsHeader';

import './ContactForm.scss';

const FORM_COMPONENTS = {
  text: {
    component: FormProvider.Input,
    props: {
      type: 'text'
    }
  },
  email: {
    component: FormProvider.Input,
    props: {
      type: 'email'
    }
  },
  date: {
    component: FormProvider.Date
  },
  textarea: {
    component: FormProvider.Input,
    props: {
      rows: 4,
      type: 'textarea'
    }
  },
  select: {
    component: FormProvider.Select
  }
};

const getReasonsFromRole = role => {
  return role === TENANT_ROLE ? TENANT_REASONS : LANDLORD_REASONS;
};

const ContactForm = () => {
  const { current: currentLocale } = useSoyuzLocales();
  const { recaptchaSiteKey } = useSoyuzClientConfig();
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);

  const {
    send,
    payload: { categoryId, role, reasonId, sectionId }
  } = useStateMachineContext();

  const {
    form: { helpCenterArticles, fields: formFields, sosNumber },
    zendesk: {
      priority,
      tags,
      subject: { prefix, suffix }
    }
  } = getReasonsFromRole(role)[reasonId];

  const schemaObject = formFields.reduce((acc, fieldId) => {
    acc[fieldId] = FORM_FIELDS[fieldId].schema;
    return acc;
  }, {});

  const formSchema = yup.object().shape(schemaObject);

  const [submitError, setSubmitError] = useState(false);
  const baseLocoKey = `contact-us.${role}`;

  const formatTicketSubject = ({ bookingId, city, rentableUnitId }) => {
    const SUFFIX_GROUPS = {
      bookingId,
      city,
      landlord: 'Landlord',
      tenant: 'Tenant',
      rentableUnitId
    };

    return `${prefix} : ${SUFFIX_GROUPS[suffix]}`;
  };

  const formatComment = ({
    rentableUnitId,
    bookingId,
    moveInDate,
    comment
  }) => {
    let formattedComment = '';

    if (rentableUnitId) {
      formattedComment = formattedComment.concat(`
        Rentable Unit Id: ${rentableUnitId}
      `);
    }
    if (bookingId) {
      formattedComment = formattedComment.concat(`
        Booking Id: ${bookingId}
      `);
    }

    if (moveInDate) {
      formattedComment = formattedComment.concat(`
        Move In Date: ${moveInDate}
      `);
    }

    if (comment) {
      formattedComment = formattedComment.concat(`
        ${comment}
      `);
    }

    return formattedComment;
  };

  const scrollTop = () => window && window.scrollTo({ top: 0 });

  const handleFormError = () => {
    Metrics.ga.sendEvent(
      'contact_form_revamp',
      'form_send',
      'validation_error'
    );
  };

  const handleFormSubmit = async values => {
    Metrics.ga.sendEvent('contact_form_revamp', 'form_send', 'attempt');
    const {
      city,
      bookingId,
      name,
      email,
      rentableUnitId,
      moveInDate,
      comment
    } = values;

    setSubmitError(false);

    try {
      await createZendeskTicket(graphqlClient())({
        input: {
          subject: formatTicketSubject({
            bookingId,
            city,
            rentableUnitId
          }),
          referenceCode: bookingId,
          name,
          email,
          tags: [
            `role:${role}`,
            ...(city ? [`city:${city}`] : []),
            `category:${categoryId}`,
            `section:${sectionId}`,
            `priority:${priority}`,
            `reason:${reasonId}`,
            ...tags
          ],
          status: 'open',
          comment: formatComment({
            rentableUnitId,
            bookingId,
            moveInDate,
            comment
          }),
          priority
        }
      });
      send('NEXT', { ...values, priority, sosNumber });
      scrollTop();
      Metrics.ga.sendEvent('contact_form_revamp', 'form_send', 'success');
    } catch (error) {
      setSubmitError(true);
      Metrics.ga.sendEvent('contact_form_revamp', 'form_send', 'error');
    }
  };

  const formatHelpCenterHint = () => {
    let articleCopy;

    if (helpCenterArticles.length > 1) {
      articleCopy = trans(`${baseLocoKey}.hint.help_center.multiple_articles`);
    } else {
      articleCopy = trans(`${baseLocoKey}.hint.help_center.single_article`);
    }

    const articleList = helpCenterArticles.map(zendeskPath => {
      const zendeskUrl = `${getPagePath('help-center')}/${zendeskPath}`;
      return (
        <a href={zendeskUrl} target="_blank" rel="noreferrer">
          {zendeskUrl}
        </a>
      );
    });

    return (
      <>
        <span>{articleCopy}</span>
        <div className="contact-form__article-list-hint">{articleList}</div>
      </>
    );
  };

  const onCaptchaSuccess = () => {
    setRecaptchaPassed(true);
  };

  const onCaptchaExpired = () => {
    setRecaptchaPassed(false);
  };

  return (
    <div className={'contact-form'}>
      <ContactUsHeader
        title={trans(`${baseLocoKey}.${reasonId}`)}
        onBack={() => send('BACK')}
      />

      {helpCenterArticles && !!helpCenterArticles.length && (
        <div className={'contact-form__description'}>
          {formatHelpCenterHint()}
        </div>
      )}

      <FormProvider
        disabled={!recaptchaPassed}
        schema={formSchema}
        onSubmit={handleFormSubmit}
        onError={handleFormError}
      >
        {formFields.map(fieldId => {
          const fieldConfig = FORM_FIELDS[fieldId];
          const Component = FORM_COMPONENTS[fieldConfig.type].component;
          return (
            <Component
              key={fieldId}
              name={fieldId}
              title={trans(`contact-us.field.${fieldId}.title`)}
              placeholder={trans(`contact-us.field.${fieldId}.placeholder`)}
              {...FORM_COMPONENTS[fieldConfig.type].props}
              {...fieldConfig.props}
            />
          );
        })}
        <div className={'contact-form__recaptcha'}>
          <ReCaptcha
            hl={currentLocale}
            sitekey={recaptchaSiteKey}
            onExpired={onCaptchaExpired}
            onChange={onCaptchaSuccess}
          />
        </div>
        <div className={'contact-form__submit-container'}>
          <div className={'contact-form__submit-button'}>
            <FormProvider.Submit>
              {trans('contact-us.form.submit')}
            </FormProvider.Submit>
          </div>
          {submitError && (
            <div className={'contact-form__submit-error'}>
              <FormError error={trans('contact-us.form-error')} />
            </div>
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export default ContactForm;
